import { BaseComponent } from '../lib/BaseComponent.js';

/**
 * Privacy Policy Page Component
 * Displays the comprehensive privacy policy
 */
export class PrivacyPolicyPage extends BaseComponent {
  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'legal-container' });

    // Header
    const header = this.createElement('div', { className: 'legal-header' });
    const backBtn = this.createElement('button', { className: 'back-btn' }, '← Back');
    backBtn.addEventListener('click', () => window.history.back());
    const title = this.createElement('h1', {}, 'Privacy Policy');
    const lastUpdated = this.createElement('p', { className: 'last-updated' }, 'Last Updated: October 31, 2025');
    header.appendChild(backBtn);
    header.appendChild(title);
    header.appendChild(lastUpdated);
    container.appendChild(header);

    // Content
    const content = this.createElement('div', { className: 'legal-content' });
    content.innerHTML = `
      <section>
        <h2>Introduction</h2>
        <p>FireGuard ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our fire extinguisher compliance tracking software platform (the "Service").</p>
        <p>This policy complies with the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and other applicable data protection laws.</p>
        <p><strong>By using our Service, you agree to the collection and use of information in accordance with this policy.</strong></p>
      </section>

      <section>
        <h2>1. Information We Collect</h2>
        <h3>1.1 Personal Information You Provide</h3>
        <p>When you register for and use our Service, we collect:</p>
        <ul>
          <li><strong>Account Information</strong>: Email address, name, password (encrypted), role within your organization</li>
          <li><strong>Organization Information</strong>: Organization name, business address, contact information</li>
          <li><strong>Location Data</strong>: Building/facility names, addresses, geographic coordinates</li>
          <li><strong>Extinguisher Information</strong>: Equipment details, QR codes, installation locations</li>
          <li><strong>Inspection Records</strong>: Inspection dates, findings, inspector names, compliance status</li>
          <li><strong>Uploaded Content</strong>: Inspection photos, documents, notes</li>
        </ul>

        <h3>1.2 Information Automatically Collected</h3>
        <p>When you access our Service, we automatically collect:</p>
        <ul>
          <li><strong>Usage Data</strong>: Pages viewed, features used, time spent, click patterns</li>
          <li><strong>Device Information</strong>: Browser type, operating system, device identifiers</li>
          <li><strong>Network Data</strong>: IP address, geographic location (country/city), ISP information</li>
          <li><strong>Performance Data</strong>: API response times, error logs, service availability metrics</li>
        </ul>
      </section>

      <section>
        <h2>2. How We Use Your Information</h2>
        <h3>2.1 Service Delivery</h3>
        <p>We use your information to:</p>
        <ul>
          <li>Provide, operate, and maintain the Service</li>
          <li>Process inspections and generate compliance reports</li>
          <li>Enable QR code scanning and equipment tracking</li>
          <li>Calculate compliance status per NFPA 10 and OSHA 1910.157</li>
          <li>Analyze inspection photos using AI technology</li>
          <li>Send compliance alerts and notifications</li>
          <li>Provide customer support and respond to inquiries</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Share Your Information</h2>
        <p>We may share your information with:</p>
        <ul>
          <li><strong>Cloudflare</strong>: Infrastructure provider (edge computing, CDN, security)</li>
          <li><strong>AI Analysis Providers</strong>: Photo analysis services (Cloudflare Workers AI)</li>
          <li><strong>Legal Requirements</strong>: Comply with legal obligations, subpoenas, court orders</li>
        </ul>
        <p><strong>All service providers are contractually bound to protect your data and use it only for specified purposes.</strong></p>
        <p><strong>We do not sell or share your personal information for advertising purposes.</strong></p>
      </section>

      <section>
        <h2>4. Data Security</h2>
        <p>We implement industry-standard security measures to protect your information:</p>
        <ul>
          <li><strong>Encryption in Transit</strong>: TLS 1.3 for all data transmission</li>
          <li><strong>Encryption at Rest</strong>: Data stored in encrypted databases (Cloudflare D1, R2)</li>
          <li><strong>Password Security</strong>: PBKDF2 hashing with 100,000 iterations, salted</li>
          <li><strong>Authentication</strong>: JWT tokens with HMAC SHA-256 signatures, 7-day expiration</li>
          <li><strong>Access Controls</strong>: Role-based access control (RBAC), multi-tenant data isolation</li>
          <li><strong>Security Headers</strong>: HSTS, CSP, X-Frame-Options, X-Content-Type-Options</li>
          <li><strong>Rate Limiting</strong>: Protection against brute force and DoS attacks (100 req/min)</li>
        </ul>
      </section>

      <section>
        <h2>5. Data Retention</h2>
        <p>We retain your information for as long as necessary to provide the Service and comply with legal obligations:</p>
        <ul>
          <li><strong>Account Information</strong>: Retained while your account is active</li>
          <li><strong>Inspection Records</strong>: Retained for 7 years (OSHA recordkeeping requirement)</li>
          <li><strong>Usage Logs</strong>: Retained for 90 days for security and debugging purposes</li>
          <li><strong>Backup Data</strong>: Retained for 30 days in encrypted backups</li>
        </ul>
      </section>

      <section>
        <h2>6. Your Privacy Rights</h2>
        <h3>6.1 GDPR Rights (EU/EEA/UK Residents)</h3>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong>: Request a copy of your personal data</li>
          <li><strong>Rectification</strong>: Correct inaccurate or incomplete data</li>
          <li><strong>Erasure</strong>: Request deletion of your data ("right to be forgotten")</li>
          <li><strong>Restriction</strong>: Limit how we process your data</li>
          <li><strong>Data Portability</strong>: Receive your data in a structured, machine-readable format</li>
          <li><strong>Object</strong>: Object to processing based on legitimate interests</li>
          <li><strong>Withdraw Consent</strong>: Withdraw consent for processing</li>
        </ul>

        <h3>6.2 CCPA/CPRA Rights (California Residents)</h3>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Know</strong>: What personal information we collect, use, disclose, and sell</li>
          <li><strong>Access</strong>: Request a copy of your personal information (twice per year)</li>
          <li><strong>Delete</strong>: Request deletion of your personal information</li>
          <li><strong>Opt-Out</strong>: Opt out of the "sale" or "sharing" of personal information (we do not sell data)</li>
          <li><strong>Correct</strong>: Request correction of inaccurate personal information</li>
          <li><strong>Non-Discrimination</strong>: Not be discriminated against for exercising your rights</li>
        </ul>

        <h3>6.3 How to Exercise Your Rights</h3>
        <p>To exercise any of these rights:</p>
        <ol>
          <li><strong>In-App</strong>: Use the Settings page > Privacy & Data section</li>
          <li><strong>Email</strong>: Send a request to privacy@fireguard.example.com</li>
        </ol>
        <p><strong>We will respond within 30 days</strong> (or as required by applicable law).</p>
      </section>

      <section>
        <h2>7. Cookies and Tracking</h2>
        <p>We use essential cookies for:</p>
        <ul>
          <li>Authentication and session management (JWT tokens)</li>
          <li>Preferences and settings (localStorage)</li>
          <li>Security (CSRF protection)</li>
        </ul>
        <p><strong>Essential cookies cannot be disabled without preventing the Service from functioning.</strong></p>
      </section>

      <section>
        <h2>8. Children's Privacy</h2>
        <p>Our Service is <strong>not intended for children under 16</strong>. We do not knowingly collect personal information from children. If you believe we have collected information from a child, contact us immediately, and we will delete it.</p>
      </section>

      <section>
        <h2>9. International Data Transfers</h2>
        <p>Your data is processed and stored on Cloudflare's global edge network. We ensure appropriate safeguards for international transfers:</p>
        <ul>
          <li>Encryption in transit and at rest</li>
          <li>Standard Contractual Clauses (SCCs) for GDPR compliance</li>
          <li>Cloudflare's SOC 2 Type II certification</li>
        </ul>
      </section>

      <section>
        <h2>10. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy to reflect changes in our data practices or legal requirements. We will notify you of material changes by:</p>
        <ul>
          <li>Email notification to your registered address</li>
          <li>Prominent notice on the Service</li>
          <li>Update to the "Last Updated" date</li>
        </ul>
        <p><strong>Continued use of the Service after changes constitutes acceptance of the updated policy.</strong></p>
      </section>

      <section>
        <h2>11. Contact Information</h2>
        <p>For privacy-related questions, concerns, or requests:</p>
        <ul>
          <li><strong>Email</strong>: privacy@fireguard.example.com</li>
          <li><strong>Support</strong>: Use the in-app support feature</li>
        </ul>
      </section>

      <section class="highlight">
        <h2>California Residents (CCPA/CPRA)</h2>
        <p><strong>We do not sell or share personal information for cross-context behavioral advertising.</strong></p>
        <p>Personal information collected includes: identifiers (name, email, IP), commercial information (usage patterns), internet activity (browsing, interactions), geolocation (facility addresses), and professional information (role, organization).</p>
        <p>Retention justification: Data retained for service delivery, legal compliance, and regulatory requirements (OSHA, NFPA).</p>
      </section>

      <section class="highlight">
        <h2>European Union Residents (GDPR)</h2>
        <p><strong>FireGuard is the data controller for your personal information.</strong></p>
        <p>Legal basis for processing: Contract performance, legitimate interests (fraud prevention, security, service improvement), legal obligation, and consent (marketing, optional features).</p>
        <p>Data transfer mechanisms: Standard Contractual Clauses (SCCs) approved by the European Commission.</p>
      </section>

      <section>
        <p class="disclaimer"><strong>By using FireGuard, you acknowledge that you have read, understood, and agree to this Privacy Policy.</strong></p>
      </section>
    `;

    container.appendChild(content);

    // Footer
    const footer = this.createElement('div', { className: 'legal-footer' });
    const footerText = this.createElement('p', {}, 'For questions or concerns, contact us at privacy@fireguard.example.com');
    footer.appendChild(footerText);
    container.appendChild(footer);

    this.shadow.appendChild(container);
  }

  private getStyles(): string {
    return `
      .legal-container {
        max-width: 900px;
        margin: 0 auto;
        padding: var(--spacing-lg);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }

      .legal-header {
        margin-bottom: var(--spacing-xl);
        padding-bottom: var(--spacing-lg);
        border-bottom: 2px solid var(--gray-200);
      }

      .back-btn {
        background: none;
        border: none;
        color: var(--primary);
        font-size: 14px;
        cursor: pointer;
        padding: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
        transition: opacity 0.2s;
      }

      .back-btn:hover {
        opacity: 0.7;
      }

      .legal-header h1 {
        font-size: 32px;
        font-weight: 700;
        color: var(--gray-900);
        margin: var(--spacing-md) 0;
      }

      .last-updated {
        font-size: 14px;
        color: var(--gray-600);
        margin: 0;
      }

      .legal-content {
        line-height: 1.7;
        color: var(--gray-800);
      }

      .legal-content section {
        margin-bottom: var(--spacing-xl);
      }

      .legal-content h2 {
        font-size: 24px;
        font-weight: 700;
        color: var(--gray-900);
        margin: var(--spacing-xl) 0 var(--spacing-md) 0;
        padding-top: var(--spacing-md);
      }

      .legal-content h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--gray-800);
        margin: var(--spacing-lg) 0 var(--spacing-sm) 0;
      }

      .legal-content p {
        margin: var(--spacing-md) 0;
        font-size: 15px;
      }

      .legal-content ul, .legal-content ol {
        margin: var(--spacing-md) 0;
        padding-left: var(--spacing-xl);
      }

      .legal-content li {
        margin: var(--spacing-sm) 0;
        font-size: 15px;
      }

      .legal-content strong {
        font-weight: 600;
        color: var(--gray-900);
      }

      .legal-content .highlight {
        background: var(--gray-50);
        border-left: 4px solid var(--primary);
        padding: var(--spacing-lg);
        margin: var(--spacing-xl) 0;
        border-radius: 4px;
      }

      .legal-content .disclaimer {
        background: var(--primary-light);
        padding: var(--spacing-lg);
        border-radius: 8px;
        text-align: center;
        font-weight: 600;
        color: var(--primary-dark);
        margin-top: var(--spacing-xl);
      }

      .legal-footer {
        margin-top: var(--spacing-xl);
        padding-top: var(--spacing-lg);
        border-top: 2px solid var(--gray-200);
        text-align: center;
        color: var(--gray-600);
        font-size: 14px;
      }

      @media (max-width: 768px) {
        .legal-container {
          padding: var(--spacing-md);
        }

        .legal-header h1 {
          font-size: 24px;
        }

        .legal-content h2 {
          font-size: 20px;
        }

        .legal-content h3 {
          font-size: 16px;
        }

        .legal-content p, .legal-content li {
          font-size: 14px;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('privacy-policy-page', PrivacyPolicyPage);
